import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { MICROSERVICES, post, put } from 'api';
import endpoints from './endpoint';
import { openAlert } from 'modules/alertHandler';

import {
  RemoveEPSEndpoint,
  RemoveCustomerEndpoint,
  RemoveAllEndpoint,
  RemoveStrategyEndpoint,
} from './interfaces';
import { showErrorResponse } from './helpers';

interface IDistributionContractsState {
  removeLoading: REQUEST_LIFE_CICLE;
  customerValidLoading: REQUEST_LIFE_CICLE;
}

const initialState: IDistributionContractsState = {
  removeLoading: REQUEST_LIFE_CICLE.IDLE,
  customerValidLoading: REQUEST_LIFE_CICLE.IDLE,
};

export const removeEPSAction = createAsyncThunk<void, RemoveEPSEndpoint>(
  'eps/remove',
  async ({ epsId }, { dispatch }) => {
    try {
      await post<boolean>(MICROSERVICES.STRATEGIES, {
        url: endpoints.removeEps(),
        data: { removeEps: epsId },
      });

      dispatch(
        openAlert({
          title: 'EPS removida com successo!',
          content:
            'A EPS selecionada foi removida com sucesso da sua distribuição. O parceiro vai receber a modificação no próximo dia útil.',
        }),
      );
    } catch (error) {
      showErrorResponse({
        alertProps: {
          title: 'Erro!',
          content: 'Ocorreu algum problema e não foi possível remover a EPS.',
        },
        errorLog: {
          error,
          errorMsg: `error at remove eps ${epsId} at distribuiton.`,
        },
      });
    }
  },
);

export const removeStrategyAction = createAsyncThunk<
  void,
  RemoveStrategyEndpoint
>('strategy/remove', async ({ strategyId }, { dispatch }) => {
  try {
    await post<boolean>(MICROSERVICES.STRATEGIES, {
      url: endpoints.removeStrategy(),
      data: { removeStrategy: strategyId },
    });

    dispatch(
      openAlert({
        title: 'Distribuição removida com successo',
        content: 'A distribuição foi removida com sucesso da estratégia.',
      }),
    );
  } catch (error) {
    showErrorResponse({
      alertProps: {
        title: 'Erro!',
        content:
          'Ocorreu algum problema e não foi possível remover a distribuição.',
      },
      errorLog: {
        error,
        errorMsg: `error at remove strategy ${strategyId}.`,
      },
    });
  }
});

export const validCustomerAction = createAsyncThunk<
  boolean,
  RemoveCustomerEndpoint
>('customer/valid', async ({ customerId }) => {
  try {
    return customerId === '123';
    // TODO: usar esse esboço abaixo quando for oficial
    // const response = await get<boolean>(MICROSERVICES.DISTRIBUTIONS, {
    //   url: endpoints.getCustomer({ customerId, distributionId }),
    // });

    // return response.data;
  } catch (error) {
    showErrorResponse({
      alertProps: {
        title: 'Erro!',
        content: 'Ocorreu algum problema e não foi possível remover o cliente.',
      },
      errorLog: {
        error,
        errorMsg: `error at remove customer ${customerId} at distribuiton.`,
      },
    });
  }
});

export const removeCustomerAction = createAsyncThunk<
  void,
  RemoveCustomerEndpoint
>('customer/remove', async ({ customerId }, { dispatch }) => {
  try {
    await post<void>(MICROSERVICES.STRATEGIES, {
      url: endpoints.removeCustomer(),
      data: { removeCustomer: customerId },
    });

    dispatch(
      openAlert({
        title: 'Cliente removido com successo',
        content:
          'O cliente selecionado foi removido com sucesso da sua distribuição.',
      }),
    );
  } catch (error) {
    showErrorResponse({
      alertProps: {
        title: 'Erro!',
        content: 'Ocorreu algum problema e não foi possível remover o cliente.',
      },
      errorLog: {
        error,
        errorMsg: `error at remove customer ${customerId}.`,
      },
    });
  }
});

export const removeAllAction = createAsyncThunk<void, RemoveAllEndpoint>(
  'all/remove',
  async ({ distributionId }, { dispatch }) => {
    try {
      await put<boolean>(MICROSERVICES.DISTRIBUTIONS, {
        url: endpoints.removeAll({ distributionId }),
      });

      dispatch(
        openAlert({
          title: 'As distribuições foram removidas com successo',
          content:
            'Todas as distribuições de todas as EPS foram removidas com sucesso',
        }),
      );
    } catch (error) {
      showErrorResponse({
        alertProps: {
          title: 'Erro!',
          content:
            'Ocorreu algum problema e não foi possível remover a distribuição.',
        },
        errorLog: {
          error,
          errorMsg: `error at remove all of distribuiton ${distributionId} contract`,
        },
      });
    }
  },
);

const thunkLoadingActions = [
  {
    action: removeEPSAction,
    loadingType: 'removeLoading',
  },
  {
    action: validCustomerAction,
    loadingType: 'customerValidLoading',
  },
  {
    action: removeCustomerAction,
    loadingType: 'removeLoading',
  },
  {
    action: removeAllAction,
    loadingType: 'removeLoading',
  },
];

const simulationModeSlice = createSlice({
  name: 'distributionContract',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    thunkLoadingActions.forEach(({ action, loadingType }) => {
      builder.addCase(action.pending, (state) => {
        state[loadingType] = REQUEST_LIFE_CICLE.PENDING;
        return state;
      });
      builder.addCase(action.rejected, (state) => {
        state[loadingType] = REQUEST_LIFE_CICLE.FAILED;
        return state;
      });
      builder.addCase(action.fulfilled, (state) => {
        state[loadingType] = REQUEST_LIFE_CICLE.SUCCEEDED;
        return state;
      });
    });
  },
});
const { reducer } = simulationModeSlice;
export default reducer;
